import { DAYS_PLACEHOLDERS_FOR_CALENDAR, MONTHS, MONTHS_SHORT, WEEKDAYS } from './constants';
import { AVAILABLE_SLOTS_TYPES } from './entries';

export const getMonthName = (monthNum: number): string => MONTHS[monthNum];

export const getMonthNumByName = (monthName: string): number => MONTHS.indexOf(monthName);

export const getMonthNameByDate = (date: Date): string => MONTHS[date.getMonth()];

type DateFormatter = (date: Date) => string | number;

const formatters: Record<string, DateFormatter> = {
  dddd: (date) => WEEKDAYS[date.getDay()],
  D: (date) => date.getDate(),
  DD: (date) => date.getDate().toString().padStart(2, '0'),
  MM: (date) => (date.getMonth() + 1).toString().padStart(2, '0'),
  MMM: (date) => MONTHS_SHORT[date.getMonth()],
  MMMM: (date) => getMonthNameByDate(date),
  YYYY: (date) => date.getFullYear(),
  T: () => 'T',
  HH: (date) => date.getHours().toString().padStart(2, '0'),
  mm: (date) => date.getMinutes().toString().padStart(2, '0'),
  ss: (date) => date.getSeconds().toString().padStart(2, '0'),
};

export const formatDate = (format: string | null | undefined, date?: string | Date): string => {
  if (!format) return 'Invalid format';

  const currentDate = date ? new Date(date) : new Date();

  if (date === '' || Number.isNaN(currentDate.getTime())) return 'Invalid date';

  const inputs: string[] = (format.match(/[a-z]+/gi) || []).map(
    (input) => String(formatters[input](currentDate)),
  );
  const separators = format.match(/[\s\-()_|:',./]/g) || [];

  return inputs.reduce((res, next, i) => res + (separators[i - 1] || '') + next);
};

export const subtractMonthsFromDate = (months: number, date?: string | Date): Date => {
  const currentDate = date ? new Date(date) : new Date();
  const currentMonth = currentDate.getMonth();
  currentDate.setMonth(currentMonth - months, currentDate.getDate());

  return currentDate;
};

export const formatToDateWithWeekDay = (date?: string | Date): string => formatDate('dddd DD MMMM YYYY', date);
export const formatToDateWithWeekDayWithoutYear = (date?: string | Date): string => formatDate('dddd DD MMMM', date);
export const formatToDateWithDashes = (date?: string | Date): string => formatDate('DD/MM/YYYY', date);

export const parseTimeSlot = (slot: string): string[] =>
  (slot.match(/\d{1,2}\w{2}/g) || []).map((time) =>
    `${time.includes('am') ? time.replace('am', '').padStart(2, '0') : parseInt(time, 10) + 12}:00`,
  );

interface TimeSlotInfo {
  range: string;
  start: string;
  end: string;
}

export const getTimeSlotInfo = (slot: string): TimeSlotInfo => {
  const [start, end] = parseTimeSlot(slot);

  return {
    range: `${start} - ${end}`,
    start,
    end,
  };
};

interface AppointmentDateInfo {
  date: string;
  time: TimeSlotInfo;
}

export const getAppointmentDateWithoutYear = (
  appointmentDate: string | Date, appointmentSlot: string,
): AppointmentDateInfo => ({
  date: formatToDateWithWeekDayWithoutYear(appointmentDate),
  time: getTimeSlotInfo(appointmentSlot),
});

export const getCurrentYear = (): number => new Date().getFullYear();

export const formatSelectedWeekdayValue = (year: number, month: string, day?: number): string => {
  const date = new Date(year, getMonthNumByName(month), day || 1);
  const dayOfWeek = date.getDay();

  if (dayOfWeek === 0) {
    date.setDate(date.getDate() + 1);
  } else if (dayOfWeek === 6) {
    date.setDate(date.getDate() + 2);
  }

  return formatDate('YYYY-MM-DD', date);
};

export const getYearForNextSelectedMonth = (month: string | number): number => {
  const monthIndex = typeof month === 'number' ? month : MONTHS.indexOf(month);
  const today = new Date();
  const currentYear = today.getFullYear();

  return monthIndex >= today.getMonth() ? currentYear : currentYear + 1;
};

interface DateAndTimeResult {
  date: string;
  dateWithYear: string;
  time: string;
}

export const getDateAndTimeForAmendAppointment = (dateAndTimeSlot: string, slotType: string): DateAndTimeResult => {
  const dateParts = dateAndTimeSlot.split(' ');
  const slotEndTime = dateParts.pop() || '';
  const slotRange = slotType === AVAILABLE_SLOTS_TYPES.HalfDay ? 5 : 2;
  const startSlotTime = (parseInt(slotEndTime, 10) - slotRange).toString().padStart(2, '0');
  const dateWithoutYear = dateParts.join(' ');

  return {
    date: dateWithoutYear,
    dateWithYear: `${dateWithoutYear} ${getYearForNextSelectedMonth(dateParts.pop() || '')}`,
    time: `${startSlotTime}:00-${slotEndTime}`,
  };
};

export const reverseDateString = (date: string, separator: string): string =>
  date.split(separator).reverse().join(separator);

export const getAlternativeDateAndSlotFormat = (formattedDate: string): string => {
  const [date, formattedSlot] = formattedDate.split(' starting between ');
  const slot = formattedSlot.split(' and ')[1];

  return `${date} ${slot}`;
};

export const formatCurrentAppointmentDate = (currentAppointment: string): Date => {
  const date = new Date(getAlternativeDateAndSlotFormat(currentAppointment));
  const appointmentYear = getYearForNextSelectedMonth(getMonthName(date.getMonth()));
  date.setFullYear(appointmentYear);

  return date;
};

interface MonthInfo {
  firstAvailableDateInMonth: number;
  daysForCalendar: number[];
  month: string;
  year: number;
}

export const getMonthInfo = (monthOffset: number, firstAvailableAppointmentDate: string | Date): MonthInfo => {
  const date = new Date(firstAvailableAppointmentDate);
  const firstAvailableDateInMonth = date.getDate();
  date.setMonth(date.getMonth() + monthOffset + 1, 0);
  const numberOfDays = date.getDate();
  date.setDate(1);
  const offsetForFirstDayInTheWeek = date.getDay() ? date.getDay() - 1 : 6;
  const daysForCalendar = DAYS_PLACEHOLDERS_FOR_CALENDAR.slice(
    0,
    7 * Math.ceil((numberOfDays + offsetForFirstDayInTheWeek) / 7),
  ).map((value, idx) => {
    const newDayValue = value + idx - offsetForFirstDayInTheWeek;
    return newDayValue <= numberOfDays ? newDayValue : -newDayValue;
  });

  return {
    firstAvailableDateInMonth,
    daysForCalendar,
    month: getMonthNameByDate(date),
    year: date.getFullYear(),
  };
};

export const getMsBeforeMidday = (): number => {
  const currentMidDayInMs = new Date().setHours(12, 0, 0);
  const timeBeforeMidday = currentMidDayInMs - Date.now();

  return timeBeforeMidday > 0 ? timeBeforeMidday : 0;
};
